'use client';

import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { AnimatePresence, motion } from 'framer-motion';
import { useTypewriter } from '@/app/hooks/useTypewriter';
import HeroParticles from '@/app/components/hero/HeroParticles';
import HeroAurora from '@/app/components/hero/HeroAurora';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Code2,
  Brain,
  Globe,
  Lightbulb,
  Star,
  Handshake,
  Mail,
  Send,
} from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { buttonStyles, iconSizes } from '@/app/constants/themeConstants';
import { useTranslation } from '@/app/features/language/hooks/useTranslation';
import { useSelector } from 'react-redux';
import { selectIsLanguageInitialized } from '@/app/store/slices/languageSlice';
import Layout from '@/app/components/layout/Layout';
import Container from '@/app/components/layout/Container';
import PublicAppShowcase from '@/app/features/workspace/components/PublicAppShowcase';
import { resolveTranslatable } from '@/app/utils/translatableBranding';

/**
 * Branding values the landing page renders. A subset of the global
 * `BrandingState` and of the tenant-branding fetch result — both satisfy it.
 */
export interface LandingBranding {
  companyName: string;
  tagline: string;
  supportEmail: string;
  /** Optional logo image URL. Shown in the hero only when present. */
  logoUrl?: string | null;
  /**
   * Hero theme. "dark" (default) keeps the space-themed dark hero. "light"
   * switches the hero to a light background — useful when the tenant's logo
   * is dark on transparent and disappears on the default dark hero.
   */
  landingTheme?: string | null;
  /**
   * Optional per-tenant marketing paragraph below the tagline. Empty / null
   * falls back to the localized `home.hero.description` string so tenants
   * that have not customised still see translated default copy.
   */
  heroDescription?: string | null;
}

interface LandingPageProps {
  /** Branding to render — operator branding for `/`, tenant branding for `/welcome/{code}`. */
  branding: LandingBranding;
  /** Destination of the "Explore Our Solution" call-to-action. */
  exploreHref: string;
}

/**
 * Shared landing-page UI (spec: tenant-landing-page). Rendered by both the
 * global SmartMatrix landing (`app/page.tsx`, exploreHref `/main`) and each
 * tenant landing (`app/welcome/[tenantCode]/page.tsx`, exploreHref the tenant
 * workspace). Layout and sections are identical across both surfaces — only
 * `branding` and `exploreHref` differ. The hero logo renders only when the
 * branding supplies a `logoUrl`, so the global `/` landing is unchanged unless
 * the operator configures one.
 */
export function LandingPage({ branding, exploreHref }: LandingPageProps) {
  const router = useRouter();
  const { t, currentLanguage } = useTranslation('home');
  const isLanguageReady = useSelector(selectIsLanguageInitialized);
  // Resolve translatable branding fields (tagline + hero description) to the
  // current locale. Tenants that store a JSON locale map get the right text;
  // legacy plain strings apply to every locale; unset fields fall back to the
  // localized i18n default.
  const resolvedTagline = useMemo(
    () => resolveTranslatable(branding.tagline, currentLanguage, branding.tagline ?? ''),
    [branding.tagline, currentLanguage],
  );
  const resolvedHeroDescription = useMemo(
    () => resolveTranslatable(branding.heroDescription, currentLanguage, t('hero.description')),
    [branding.heroDescription, currentLanguage, t],
  );
  const [isClient, setIsClient] = useState(false);
  // cursor: caret blinks ~3 times, no text yet
  // typing: typewriter runs on tagline
  // reveal: logo + company name + description + button fade in
  const [heroPhase, setHeroPhase] = useState<'cursor' | 'typing' | 'reveal'>('cursor');
  // Hold the entire animation chain until theme CSS vars are confirmed applied.
  const [themeReady, setThemeReady] = useState(false);

  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.documentElement.getAttribute('data-theme-booted') === '1') {
      setThemeReady(true);
      return;
    }
    const raf = requestAnimationFrame(() => setThemeReady(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Cursor blinks for ~1.5s (3 blinks at 500ms each), then typewriter starts.
  // Wait for both theme CSS vars and user language preference before animating.
  useEffect(() => {
    if (!themeReady || !isLanguageReady || heroPhase !== 'cursor') return;
    const timer = setTimeout(() => setHeroPhase('typing'), 1500);
    return () => clearTimeout(timer);
  }, [themeReady, isLanguageReady, heroPhase]);

  // CJK characters carry more meaning per glyph than Latin letters, so a
  // 45 ms-per-char delay (tuned for English) makes Chinese / Japanese
  // taglines feel rushed — they finish in roughly half the time of an
  // equivalent English tagline. Bump the delay for CJK languages so the
  // overall typing duration is comparable across locales.
  const typingDelay =
    currentLanguage === 'zh-CN' ||
    currentLanguage === 'zh-TW' ||
    currentLanguage === 'ja-JP'
      ? 105
      : 45;
  const { displayed: taglineText, done: taglineDone } = useTypewriter(
    resolvedTagline,
    typingDelay,
    0,
    heroPhase === 'typing' || heroPhase === 'reveal',
  );
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
  });

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!taglineDone || heroPhase !== 'typing') return;
    const timer = setTimeout(() => setHeroPhase('reveal'), 350);
    return () => clearTimeout(timer);
  }, [taglineDone, heroPhase]);

  // Simple animation hooks using react-intersection-observer
  const { ref: heroRef } = useInView({ triggerOnce: true, threshold: 0.1 });
  const { ref: servicesRef, inView: servicesInView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const { ref: valuesRef, inView: valuesInView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const { ref: contactRef, inView: contactInView } = useInView({ triggerOnce: true, threshold: 0.1 });

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setContactForm({ name: '', email: '', message: '' });
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setContactForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  if (!isClient || !isLanguageReady) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-16">
        {/* Pulsing spinner ring — matches the login page loading style */}
        <div className="relative flex items-center justify-center">
          <div className="absolute w-20 h-20 rounded-full border-4 border-primary/20 animate-ping" style={{ animationDuration: '2s' }} />
          <div className="absolute w-20 h-20 rounded-full border-4 border-transparent border-t-primary animate-spin" style={{ animationDuration: '1s' }} />
          <div className="w-10 h-10 rounded-full bg-primary/10 backdrop-blur-sm" />
        </div>
      </div>
    );
  }

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number],
      },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number],
      },
    },
  };

  const services = [
    {
      icon: <Code2 className={`${iconSizes.large} text-primary`} />,
      title: t('services.softwareDev.title'),
      description: t('services.softwareDev.description'),
    },
    {
      icon: <Brain className={`${iconSizes.large} text-accent`} />,
      title: t('services.aiSolutions.title'),
      description: t('services.aiSolutions.description'),
    },
    {
      icon: <Globe className={`${iconSizes.large} text-ring`} />,
      title: t('services.webApps.title'),
      description: t('services.webApps.description'),
    },
  ];

  const values = [
    {
      icon: <Lightbulb className={`${iconSizes.large} text-primary`} />,
      title: t('values.innovation.title'),
      description: t('values.innovation.description'),
    },
    {
      icon: <Star className={`${iconSizes.large} text-accent`} />,
      title: t('values.quality.title'),
      description: t('values.quality.description'),
    },
    {
      icon: <Handshake className={`${iconSizes.large} text-ring`} />,
      title: t('values.collaboration.title'),
      description: t('values.collaboration.description'),
    },
  ];

  // Per-tenant hero theme. "light" flips the dark space-themed hero to a light
  // background — useful when the tenant's logo is dark on transparent and would
  // disappear on the default dark hero (e.g. TLT's mLinkOne wordmark). Default
  // "dark" preserves the original look for the global landing and any tenant
  // that has not set the override (spec: tenant-branding-settings, branding.landing_theme).
  const isLightHero = branding.landingTheme === 'light';

  return (
    <Layout
      showHeader={true}
      showFooter={true}
      headerAutoHide={false}
      showBreadcrumb={false}
      containerSize="full"
      containerPadding="none"
      density="default"
    >
      {/* Hero Section */}
      <div
        className={`min-h-screen flex items-center relative overflow-hidden ${
          isLightHero ? 'bg-slate-50' : 'bg-slate-950'
        }`}
        style={{
          backgroundImage: isLightHero
            ? 'radial-gradient(ellipse at top, hsl(var(--primary) / 0.10), transparent 60%), radial-gradient(ellipse at bottom right, hsl(var(--accent) / 0.08), transparent 55%)'
            : 'radial-gradient(ellipse at top, hsl(var(--primary) / 0.18), transparent 60%), radial-gradient(ellipse at bottom right, hsl(var(--accent) / 0.15), transparent 55%)',
        }}
      >
        {/* HeroAurora is a soft blurred glow tuned for the dark space theme;
            on a light background it just adds a faint haze, so keep it
            dark-only. */}
        {!isLightHero && <HeroAurora />}
        {/* Particles render in both modes — the canvas component switches
            from additive blending (dark) to regular alpha (light) so the
            colored dots stay visible on a light background. */}
        <HeroParticles theme={isLightHero ? 'light' : 'dark'} />
        {/* Vignette for focus — dark theme only (would dim the light bg). */}
        {!isLightHero && (
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,rgba(0,0,0,0.55)_100%)] pointer-events-none" />
        )}

        <Container size="xl" padding="lg" className="relative z-10">
          <div ref={heroRef} className="text-center flex flex-col items-center">
            {/* 0. Tenant logo — rendered only when branding supplies one, so the
                global `/` landing is unchanged unless the operator sets one. */}
            {branding.logoUrl && (
              <div className="min-h-[4rem] md:min-h-[5rem] mb-6 md:mb-8 flex items-center justify-center">
                <AnimatePresence>
                  {heroPhase === 'reveal' && (
                    <motion.img
                      key="landing-logo"
                      src={branding.logoUrl}
                      alt={branding.companyName}
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.7, ease: 'easeOut' }}
                      className="h-16 md:h-20 w-auto object-contain"
                    />
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* 1. Company name — shown only when there is no logo. The logo
                and the name are alternatives (one identity mark, never both). */}
            {!branding.logoUrl && (
              <div className="min-h-[3rem] md:min-h-[3.5rem] mb-10 md:mb-14 flex items-center justify-center">
                <AnimatePresence>
                  {heroPhase === 'reveal' && (
                    <motion.div
                      key="company-name"
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.7, ease: 'easeOut' }}
                      className="text-3xl md:text-4xl font-extrabold tracking-tighter"
                    >
                      <span
                        className={`bg-gradient-to-r ${
                          isLightHero
                            ? 'from-slate-900/80 via-[hsl(var(--primary))] to-[hsl(var(--accent))]'
                            : 'from-white/60 via-[hsl(var(--primary))] to-[hsl(var(--accent))]'
                        } bg-clip-text text-transparent`}
                      >
                        {branding.companyName}
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* 2. Tagline — typewriter. text-balance distributes lines
                evenly when the translated tagline is too long for a single
                line (French / Spanish translations are ~25-30% longer than
                English and would overflow `max-w-6xl` if forced single-line).
                Layout uses a CSS-grid stack: an invisible copy of the FINAL
                tagline reserves the full wrapped height; the visible
                typewriter overlays the same grid cell and aligns to the top.
                The container size is fixed from the first frame, so the
                description + CTA below never shift while the typewriter
                animates across the wrap threshold. */}
            <h1
              className={`text-3xl md:text-5xl lg:text-6xl font-medium tracking-tight max-w-6xl lg:max-w-[110rem] text-balance leading-[1.15] mb-8 md:mb-12 min-h-[5rem] md:min-h-[5.5rem] lg:min-h-[6rem] grid ${
                isLightHero ? 'text-slate-900 landing-hero-light-text' : 'text-white'
              }`}
              // `landing-hero-light-text` is a marker class consumed by a
              // higher-specificity !important rule in globals.css. The base
              // `.dark * { color: #fff !important }` rule there is too broad
              // and would otherwise turn the tagline white even when the
              // tenant's landing_theme = 'light' (cream hero background).
              // The marker class explicitly opts the hero out, locking the
              // text to slate-900 regardless of the global UI dark theme.
            >
              {/* Invisible — reserves the final wrapped size. Hidden from
                  screen readers (the visible typewriter is read instead). */}
              <span
                aria-hidden="true"
                className="invisible col-start-1 row-start-1 pointer-events-none select-none"
              >
                {resolvedTagline}
              </span>
              {/* Visible typewriter, overlaying the same grid cell. */}
              <span className="col-start-1 row-start-1 self-start">
                <span>{taglineText}</span>
                <span
                  className={`inline-block w-[2px] md:w-[3px] h-[0.85em] align-middle ml-2 bg-[hsl(var(--primary))] ${
                    heroPhase === 'reveal' ? 'opacity-0' : ''
                  }`}
                  style={{
                    boxShadow: '0 0 12px hsl(var(--primary))',
                    ...(heroPhase !== 'reveal'
                      ? { animation: 'cursor-blink 0.5s step-end 3' }
                      : {}),
                  }}
                />
              </span>
            </h1>

            {/* 3. Description + CTA — fade in during reveal */}
            <div className="min-h-[8rem] md:min-h-[7rem]">
              <AnimatePresence>
                {heroPhase === 'reveal' && (
                  <motion.div
                    key="hero-rest"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.25, ease: 'easeOut' }}
                  >
                    <p
                      className={`text-base md:text-lg mb-10 max-w-2xl mx-auto leading-relaxed ${
                        isLightHero
                          ? 'text-slate-700 landing-hero-light-text-muted'
                          : 'text-white/65'
                      }`}
                    >
                      {/* Translatable: branding.hero_description may be a
                          JSON locale map ({"en-US":"…","zh-TW":"…"}) or a
                          legacy plain string. resolveTranslatable picks the
                          current-locale value with graceful fallback chain;
                          when the field is unset the i18n localized default
                          is shown. */}
                      {resolvedHeroDescription}
                    </p>

                    <Button
                      size="lg"
                      onClick={() => router.push(exploreHref)}
                      className={buttonStyles.primary}
                      variant={null as any}
                    >
                      {t('hero.exploreButton')}
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </Container>
      </div>

      {/* Services Section */}
      <div className="py-24 bg-background">
        <Container size="xl" padding="lg">
          <motion.div
            ref={servicesRef}
            initial="hidden"
            animate={servicesInView ? 'visible' : 'hidden'}
            variants={staggerContainer}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 text-foreground">
              {t('services.title')}
            </h2>

            <p className="text-xl text-center mb-16 text-muted-foreground max-w-3xl mx-auto">
              {t('services.subtitle')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <motion.div key={index} variants={staggerItem}>
                  <Card className="h-full p-6 text-center border-2 border-border/50 rounded-2xl bg-gradient-to-br from-background/90 to-background/70 backdrop-blur-sm shadow-lg hover:shadow-xl hover:-translate-y-2 transition-all duration-300">
                    <CardContent className="p-0">
                      <div className="mb-6">{service.icon}</div>
                      <h3 className="text-2xl font-semibold mb-4 text-foreground">
                        {service.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {service.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </Container>
      </div>

      {/* Values Section */}
      <div className="py-24 bg-muted/30">
        <Container size="xl" padding="lg">
          <motion.div
            ref={valuesRef}
            initial="hidden"
            animate={valuesInView ? 'visible' : 'hidden'}
            variants={staggerContainer}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 text-foreground">
              {t('values.title')}
            </h2>

            <p className="text-xl text-center mb-16 text-muted-foreground max-w-3xl mx-auto">
              {t('values.subtitle')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <motion.div key={index} variants={staggerItem}>
                  <div className="text-center p-6">
                    <div className="mb-6">{value.icon}</div>
                    <h3 className="text-2xl font-semibold mb-4 text-foreground">
                      {value.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </Container>
      </div>

      {/* Public App Showcase */}
      <PublicAppShowcase />

      {/* Contact Section */}
      <div className="pt-24 pb-12 bg-background">
        <Container size="xl" padding="lg">
          <motion.div
            ref={contactRef}
            initial="hidden"
            animate={contactInView ? 'visible' : 'hidden'}
            variants={fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 text-foreground">
              {t('contact.title')}
            </h2>

            <p className="text-xl text-center mb-16 text-muted-foreground max-w-3xl mx-auto">
              {t('contact.subtitle')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-2xl font-semibold mb-6 text-foreground">
                  {t('contact.info.title')}
                </h3>

                <div className="space-y-4 mb-8">
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">{t('contact.info.email')}:</strong>{' '}
                    {branding.supportEmail}
                  </p>
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">{t('contact.info.phone')}:</strong> +1 (555) 123-4567
                  </p>
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">{t('contact.info.location')}:</strong> San Francisco, CA
                  </p>
                </div>

                <Button size="lg" className={buttonStyles.primary} variant={null as any}>
                  <Mail className={`${iconSizes.button} mr-2`} />
                  {t('contact.getInTouch')}
                </Button>
              </div>

              <Card className="p-8 rounded-2xl bg-gradient-to-br from-background/90 to-background/70 backdrop-blur-sm border-2 border-border/50 shadow-lg">
                <CardHeader className="p-0 mb-6">
                  <CardTitle className="text-2xl font-semibold text-foreground">
                    {t('contact.form.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <form onSubmit={handleContactSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-foreground mb-2 block">
                        {t('contact.form.name')}
                      </Label>
                      <Input
                        id="name"
                        value={contactForm.name}
                        onChange={handleInputChange('name')}
                        required
                        placeholder={t('contact.form.namePlaceholder')}
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-foreground mb-2 block">
                        {t('contact.form.email')}
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={contactForm.email}
                        onChange={handleInputChange('email')}
                        required
                        placeholder={t('contact.form.emailPlaceholder')}
                      />
                    </div>
                    <div>
                      <Label htmlFor="message" className="text-foreground mb-2 block">
                        {t('contact.form.message')}
                      </Label>
                      <Textarea
                        id="message"
                        rows={4}
                        value={contactForm.message}
                        onChange={(e) =>
                          setContactForm((prev) => ({ ...prev, message: e.target.value }))
                        }
                        required
                        placeholder={t('contact.form.messagePlaceholder')}
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className={buttonStyles.fullWidth}
                      variant={null as any}
                    >
                      <Send className={`${iconSizes.button} mr-2`} />
                      {t('contact.form.sendButton')}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </Container>
      </div>
    </Layout>
  );
}

export default LandingPage;
